import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LakshLogo } from '@/components/LakshLogo';
import { toast } from 'sonner';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import axios from 'axios';
import { getErrorMessage } from '@/utils/helpers';
import {
  Users,
  UserPlus,
  Search,
  Eye,
  LayoutDashboard,
  Calendar,
  IndianRupee,
  Briefcase,
  LogOut,
  Mail,
  Building2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (num) => {
  if (num >= 10000000) return `₹${(num/10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num/100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/ca/dashboard' },
  { icon: Users, label: 'Clients', path: '/ca/clients' },
  { icon: Calendar, label: 'Compliance', path: '/ca/compliance' },
  { icon: IndianRupee, label: 'Revenue', path: '/ca/revenue' },
];

export default function CAClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    business_name: ''
  });
  const [inviting, setInviting] = useState(false);
  const [user, setUser] = useState(null);
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('laksh_ca_token');
    const userData = localStorage.getItem('laksh_ca_user');
    const firmData = localStorage.getItem('laksh_ca_firm');
    
    if (!token) {
      navigate('/ca/login');
      return;
    }
    
    if (userData) setUser(JSON.parse(userData));
    if (firmData) setFirm(JSON.parse(firmData));
    
    fetchClients(token);
  }, [navigate]);

  const fetchClients = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ca/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Clients error:', error);
      if (error.response?.status === 401) {
        navigate('/ca/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('laksh_ca_token');
    
    setInviting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ca/clients/invite`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(data.message);
      setDialogOpen(false);
      setInviteData({ client_name: '', client_email: '', client_phone: '', business_name: '' });
      fetchClients(token);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send invitation'));
    } finally {
      setInviting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('laksh_ca_token');
    localStorage.removeItem('laksh_ca_user');
    localStorage.removeItem('laksh_ca_firm');
    navigate('/ca/login');
  };

  const filteredClients = clients.filter(c =>
    c.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.client_email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06100A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0a1a12] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <LakshLogo height={32} />
          <div className="mt-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#1D9E75]" />
            <span className="text-xs text-[#1D9E75] font-medium">CA Portal</span>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              data-testid={`ca-nav-${item.label.toLowerCase()}`}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-gray-400 transition-colors hover:bg-white/5 hover:text-white ${
                item.path === '/ca/clients' ? 'bg-[#1D9E75]/20 text-[#1D9E75]' : ''
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-full flex items-center justify-center">
              <span className="text-[#1D9E75] font-semibold">{user?.name?.charAt(0) || 'C'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{firm?.name}</p>
            </div>
          </div>
          <FeedbackDialog portal="ca" token={localStorage.getItem('laksh_ca_token')} triggerClassName="w-full px-4 py-2 mb-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5" label="Send feedback" />
          <Button data-testid="ca-logout-btn" variant="ghost" onClick={handleLogout} className="w-full text-gray-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white font-['Outfit']">Clients</h1>
              <p className="text-gray-400">Manage and monitor your client portfolio</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="ca-invite-client-btn" className="bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white gap-2">
                  <UserPlus className="w-4 h-4" /> Invite Client
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a1a12] border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white">Invite New Client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Client Name</Label>
                    <Input data-testid="ca-invite-client-name"
                      value={inviteData.client_name}
                      onChange={(e) => setInviteData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="John Doe"
                      className="bg-[#06100A] border-white/20 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Email</Label>
                    <Input data-testid="ca-invite-client-email"
                      type="email"
                      value={inviteData.client_email}
                      onChange={(e) => setInviteData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="client@business.com"
                      className="bg-[#06100A] border-white/20 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Phone</Label>
                    <Input data-testid="ca-invite-client-phone"
                      value={inviteData.client_phone}
                      onChange={(e) => setInviteData(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="bg-[#06100A] border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Business Name</Label>
                    <Input data-testid="ca-invite-business-name"
                      value={inviteData.business_name}
                      onChange={(e) => setInviteData(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Acme Corp"
                      className="bg-[#06100A] border-white/20 text-white"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button data-testid="ca-invite-cancel" type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">
                      Cancel
                    </Button>
                    <Button data-testid="ca-invite-submit" type="submit" disabled={inviting} className="flex-1 bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white">
                      {inviting ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input data-testid="ca-clients-search"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#0a1a12] border-white/10 text-white"
            />
          </div>

          {/* Clients Table */}
          {filteredClients.length > 0 ? (
            <Card className="bg-[#0a1a12] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400">Business</TableHead>
                    <TableHead className="text-gray-400">Contact</TableHead>
                    <TableHead className="text-gray-400">Plan</TableHead>
                    <TableHead className="text-gray-400 text-right">Revenue</TableHead>
                    <TableHead className="text-gray-400 text-right">Outstanding</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id} className="border-white/10 hover:bg-white/5">
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{client.business_name}</p>
                          {client.gstin && <p className="text-xs text-gray-500 font-mono">{client.gstin}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-gray-300">{client.client_name}</p>
                          <p className="text-xs text-gray-500">{client.client_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          client.plan === 'scale' ? 'bg-[#EF9F27]/20 text-[#EF9F27]' :
                          client.plan === 'growth' ? 'bg-blue-500/20 text-blue-400' :
                          client.plan === 'starter' ? 'bg-green-500/20 text-green-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {client.plan}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-white">
                        {formatCurrency(client.total_revenue)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#EF9F27]">
                        {formatCurrency(client.outstanding_receivables)}
                      </TableCell>
                      <TableCell>
                        <span data-testid={`ca-client-status-${client.id}`} className={`px-2 py-1 rounded text-xs ${
                          client.status === 'active' ? 'bg-green-500/20 text-green-400' : client.status === 'pending' ? 'bg-[#EF9F27]/20 text-[#EF9F27]' : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {client.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {client.status !== 'pending' && (
                          <Link data-testid={`ca-client-view-${client.id}`} to={`/ca/clients/${client.id}`}>
                            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card className="bg-[#0a1a12] border-white/10 p-12">
              <div className="text-center">
                <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No clients yet</h3>
                <p className="text-gray-400 mb-6">Invite your first client to get started</p>
                <Button data-testid="ca-invite-empty-btn" onClick={() => setDialogOpen(true)} className="bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white gap-2">
                  <UserPlus className="w-4 h-4" /> Invite Client
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
